import { Component, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EOrdenacao } from 'src/app/shared/data/ordenacao.enum';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { SessionService } from 'src/app/shared/services/session.service';
import { canPickCompany } from 'src/app/shared/utilities/company-scope';
import { canListCompanies } from 'src/app/shared/utilities/can-list-companies';
import { AuditLogService } from 'src/app/shared/services/audit-log.service';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { UnavailableRequestsReportsService } from './services/unavailable-requests-reports.service';
import { IUnavailableRequestsReports, IFilterUnavailableRequestsReports } from './data/unavailable-requests-reports';
import { CompanyService } from '../companies/services/company.service';
import { ICompany } from '../companies/models/company.interface';
import { ModalDisclaimerComponent } from '../attendance-reports/components/modal-disclaimer/modal-disclaimer.component';
import { ToastService } from 'src/app/shared/services/toast.service';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { interceptorAvisouErro } from 'src/app/shared/http-interceptor/http-interceptor.module';

@Component({
  selector: 'app-unavailable-requests-reports',
  templateUrl: './unavailable-requests-reports.component.html',
  styleUrls: ['./unavailable-requests-reports.component.scss'],
  providers: [DatePipe]
})
export class UnavailableRequestsReportsComponent implements OnInit, OnDestroy {
  ERoleAdminString = ERoleAdminString;
  private readonly destroy$ = new Subject<void>();
  private reportsSub?: Subscription;
  unavailableRequestsReports: IUnavailableRequestsReports[] = [];
  orderControl = new UntypedFormControl();
  companyControl = new UntypedFormControl();

  listOrder = [
    {
      value: EOrdenacao.AlfabeticaAZ,
      label: 'Alfabética - de A a Z'
    },
    {
      value: EOrdenacao.AlfabeticaZA,
      label: 'Alfabética - de Z a A'
    },
    {
      value: 3,
      label: 'Mais recentes'
    },
    {
      value: 4,
      label: 'Mais antigas'
    }
  ];

  dadosPaginacao = {
    ordenacao: EOrdenacao.AlfabeticaAZ,
    paginaSelecionada: 0,
    tamanhoPagina: 10,
    totalPaginas: 0,
    itensTotal: 0
  };

  companyId?: string;
  partnerId?: string;
  accountRole?: string;
  acaoInput = new UntypedFormControl();
  showCalendar = false;
  selectedDate: string | null = null;
  filterStartDate: Date | null = null;
  filterEndDate: Date | null = null;
  range = new UntypedFormGroup({
    start: new UntypedFormControl(null),
    end: new UntypedFormControl(null)
  });
  companies: ICompany[] = [];
  canFilterByCompany = false;

  constructor(
    private readonly unavailableRequestsReportsService: UnavailableRequestsReportsService,
    private readonly sessionService: SessionService,
    private readonly datePipe: DatePipe,
    private readonly companyService: CompanyService,
    private readonly matDialog: MatDialog,
    private readonly auditLogService: AuditLogService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.companyId = this.sessionService.getSession().companyId;
    this.partnerId = this.sessionService.getSession().partnerId;
    this.accountRole = this.sessionService.getSession().accountRole;

    // Interno e parceiro sem empresa escolhem a empresa; usuário de empresa vê só a dele. As empresas só vêm
    // com a permissão do Company/ListActive.
    const session = this.sessionService.getSession();
    this.canFilterByCompany = canPickCompany(session) && canListCompanies(session);

    this.getUnavailableRequestsReports();
    if (this.canFilterByCompany) {
      this.getCompanies();
    }

    this.orderControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((ordenacaoSelecionada: number) => {
      this.dadosPaginacao.ordenacao = ordenacaoSelecionada;
      this.getUnavailableRequestsReports();
    });
    this.companyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getUnavailableRequestsReports();
    });
    this.range.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ start, end }: { start: Date | null; end: Date | null }) => {
        if (start && end) {
          this.dadosPaginacao.paginaSelecionada = 0;
          this.getUnavailableRequestsReports();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getUnavailableRequestsReports(): void {
    this.filterStartDate = this.range.controls['start'].value ?? null;
    this.filterEndDate = this.range.controls['end'].value ?? null;

    const filter: IFilterUnavailableRequestsReports = {
      pageNumber: Number(this.dadosPaginacao.paginaSelecionada),
      pageSize: Number(this.dadosPaginacao.tamanhoPagina)
    };

    if (this.dadosPaginacao.ordenacao) {
      filter.orderBy = Number(this.dadosPaginacao.ordenacao);
    }

    filter.companyId = this.companyControl.value || '';

    if (this.partnerId) {
      filter.partnerId = this.partnerId;
    }

    if (this.accountRole) {
      filter.accountRole = parseInt(this.accountRole);
    }
    if (this.acaoInput.value) {
      filter.searchText = String(this.acaoInput.value);
    }
    if (this.selectedDate) {
      filter.selectedDate = this.selectedDate;
    }

    if (this.filterStartDate) {
      filter.startDate = this.datePipe.transform(this.filterStartDate, 'yyyy-MM-dd HH:mm:ss.SSS') ?? undefined;
    }
    if (this.filterEndDate) {
      const endOfDay = new Date(this.filterEndDate);
      endOfDay.setHours(23, 59, 59, 999);
      filter.endDate = this.datePipe.transform(endOfDay, 'yyyy-MM-dd HH:mm:ss.SSS') ?? undefined;
    }

    // Cancela a busca anterior: uma resposta atrasada de outro filtro não sobrescreve a atual.
    this.reportsSub?.unsubscribe();
    this.reportsSub = this.unavailableRequestsReportsService
      .getUnavailableRequestsReports(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.unavailableRequestsReports = res.data;
        this.dadosPaginacao.totalPaginas = Math.ceil(res.count! / this.dadosPaginacao.tamanhoPagina);
        this.dadosPaginacao.itensTotal = res.count!;
      });
  }

  public cleanFilter(): void {
    this.acaoInput.reset();
    this.selectedDate = null;
    this.filterStartDate = null;
    this.filterEndDate = null;
    this.range.reset();
    this.companyControl.reset();
    this.orderControl.reset();
    this.getUnavailableRequestsReports();
  }

  public mudouPagina(paginaSelecionada: number): void {
    this.dadosPaginacao.paginaSelecionada = paginaSelecionada;
    this.getUnavailableRequestsReports();
  }

  public mudouTamanhoPagina(itensPaginaSelecionada: number): void {
    this.dadosPaginacao.tamanhoPagina = itensPaginaSelecionada;
    this.getUnavailableRequestsReports();
  }

  public downloadExcel(): void {
    this.matDialog
      .open(ModalDisclaimerComponent, { width: '500px', panelClass: 'bg-iceberg', disableClose: true })
      .afterClosed()
      .subscribe((aceito: boolean) => {
        if (!aceito) return;

        this.auditLogService.logDisclaimerAccepted(this.sessionService.getSession());

        const filter: IFilterUnavailableRequestsReports = {
          pageNumber: Number(this.dadosPaginacao.paginaSelecionada),
          pageSize: Number(this.dadosPaginacao.tamanhoPagina)
        };

        filter.companyId = this.companyControl.value || '';

        if (this.partnerId) {
          filter.partnerId = this.partnerId;
        }

        if (this.accountRole) {
          filter.accountRole = parseInt(this.accountRole);
        }

        if (this.acaoInput.value) {
          filter.searchText = String(this.acaoInput.value);
        }

        if (this.selectedDate) {
          filter.selectedDate = this.selectedDate;
        }

        if (this.filterStartDate) {
          filter.startDate = this.datePipe.transform(this.filterStartDate, 'yyyy-MM-dd HH:mm:ss.SSS') ?? undefined;
        }

        if (this.filterEndDate) {
          const endOfDay = new Date(this.filterEndDate);
          endOfDay.setHours(23, 59, 59, 999);
          filter.endDate = this.datePipe.transform(endOfDay, 'yyyy-MM-dd HH:mm:ss.SSS') ?? undefined;
        }

        this.unavailableRequestsReportsService.exportarExcelUnavailableRequestsReports(filter);
      });
  }

  public getUnavailableRequestsByDate(date: Date): void {
    this.selectedDate = this.datePipe.transform(date, 'yyyy-MM-dd');
    this.getUnavailableRequestsReports();
  }

  private getCompanies(): void {
    this.companyService
      .listarEmpresasAtivas()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resposta) => {
          this.companies = resposta.data;
        },
        error: (erro) => {
          this.companies = [];
          if (!interceptorAvisouErro(erro)) {
            this.toastService.toast('Erro ao carregar as empresas. Tente novamente.', EMessageType.Error);
          }
        }
      });
  }

  public toggleCalendar(): void {
    this.showCalendar = !this.showCalendar;
  }
}
